import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';

import { Contract } from '~/data';
import { ResponseError } from '~/data/api';

import { useContractAnnexes } from '../useContractAnnexes/useContractAnnexes';
import { useContractFile } from '../useContractFile/useContractFile';
import { cacheKeys } from '../useContracts/useContracts';

interface ContractsData {
  pages: { items: Contract[] }[];
}

export const useContract = ({ contractNumber }: { contractNumber: string }) => {
  const queryClient = useQueryClient();

  const contract = useSuspenseQuery<Contract | undefined, ResponseError>({
    queryFn: () =>
      queryClient
        .getQueryData<ContractsData>(['contracts'])
        ?.pages[0].items.find((c) => c.contractNumber === contractNumber),
    queryKey: cacheKeys.detail(contractNumber),
  });

  const contractFile = useContractFile({ contractNumber });

  const annexes = useContractAnnexes({ contractNumber });

  return {
    data: {
      annexes: {
        items: annexes.data?.items || [],
        total: annexes.data?.total || 0,
      },
      contract: contract.data,
      contractFile: {
        blob: contractFile.data,
        downloadUrl: contractFile.downloadUrl,
      },
    },
    error: contract.error || annexes.error || contractFile.error,
    isContractFileError: contractFile.isError,
    isContractFileLoading: contractFile.isLoading,
    isError: contract.isError || annexes.isError || contractFile.isError,
    isLoading: contract.isLoading || annexes.isLoading,
    isSuccess: contract.isSuccess && annexes.isSuccess && contractFile.isSuccess,
  };
};
